import { effect, Injectable, signal } from '@angular/core';
import { environment } from '../../environments/environment';

const DEFAULT_PLACEHOLDER_IMAGE = '/assets/images/default-section.png';

// Injected by `ng build --define` in build-single.sh; undefined under ng serve and tests.
declare const BUILD_VERSION: string | undefined;

@Injectable({
  providedIn: 'root',
})
export class ThemeService {

  currentTheme = signal<string | null>(null);
  landingWallpaper = signal<string | null>(null);
  // Item/section image placeholder: the theme's own default-placeholder.png when it
  // exists, otherwise the bundled default. Resolved once per theme via probeThemePlaceholder().
  placeholderImage = signal<string>(DEFAULT_PLACEHOLDER_IMAGE);

  /**  Set theme dynamically */
  setTheme(themeName: string | null): void {
    // 'default' matches the 'default-theme' style bundle in angular.json — there is no
    // bundle for any other fallback name.
    const resolvedTheme = themeName ?? 'default';

    if (resolvedTheme !== this.currentTheme()) {
      this.currentTheme.set(resolvedTheme);
      this.applyTheme(resolvedTheme);
      this.probeThemePlaceholder(resolvedTheme);
    }
  }

  // Not every theme ships a placeholder, so preload it and only switch over once it has
  // actually loaded - lazyLoad's errorImage has no further fallback if it 404s itself.
  private probeThemePlaceholder(themeName: string): void {
    this.placeholderImage.set(DEFAULT_PLACEHOLDER_IMAGE);

    const url = `${environment.urls.assets}/${themeName}/default-placeholder.png`;
    const img = new Image();
    img.onload = () => {
      // Ignore a late result for a theme that has since been replaced.
      if (this.currentTheme() === themeName) {
        this.placeholderImage.set(url);
      }
    };
    img.src = url;
  }

  setLandingWallpaper(wallpaper: string): void {
    const img = new Image();
    img.src = wallpaper;

    img.onload = () => {
      this.landingWallpaper.set(wallpaper);
    };

    img.onerror = () => {
      console.clear();
      this.landingWallpaper.set(null);
    };
  }

  /**  Apply the selected theme */
  private applyTheme(themeName: string): void {
    const themeId = 'theme-style';
    // Theme bundles are inject:false, so Angular doesn't hash their names and they're
    // served with a 1-year cache. The per-build ?v= makes each release a new URL.
    const version = typeof BUILD_VERSION !== 'undefined' ? BUILD_VERSION : '';
    const themePath = `${themeName}-theme.css${version ? `?v=${version}` : ''}`;

    let existingLink = document.getElementById(themeId) as HTMLLinkElement;

    //  Remove old styles if they exist
    if (existingLink) {
      existingLink.parentNode?.removeChild(existingLink);
    }

    //  Add the new theme
    const link = document.createElement('link');
    link.id = themeId;
    link.rel = 'stylesheet';
    link.href = themePath;
    link.onload = () => console.log(` Theme applied: ${themeName}`);
    link.onerror = () => console.error(`Theme load failed: ${themePath}`);

    document.head.appendChild(link);
  }
}